import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { PreparedSortCriteria, SortDirection } from '@steelbuy/api-integration';
import {
    Definition,
    Product,
    getMaterialTypes,
    CoatingWeight,
    Compliance,
    Finish,
    Grade,
    GradeFamily,
    MaterialType,
    Specification,
    SurfaceFinish,
    SurfaceTreatment,
    Temper,
    Shape,
    AddressState,
    ThicknessType,
} from '@steelbuy/ts-shared';

export type DashboardFormData = {
    materialType: MaterialType;
    shape: Shape[];
    definition?: Definition;
    product?: Product[];
    grade?: Grade[];
    specification?: Specification[];
    temper?: Temper[];
    finish?: Finish[];
    gradeFamily?: GradeFamily[];
    coatingWeight?: CoatingWeight[];
    surfaceTreatment?: SurfaceTreatment[];
    compliance?: Compliance[];
    surfaceFinish?: SurfaceFinish[];
    sort?: PreparedSortCriteria;
    quality?: 'prime' | 'nonPrime';
    location?: AddressState;
    gauge?: number[];
    thicknessType?: ThicknessType;
    dimensions?: {
        width?: number;
        length?: number;
    };
    dimensionRanges?: {
        maxWidth?: number;
        minWidth?: number;
        maxLength?: number;
        minLength?: number;
    };
};

export interface DashboardFormContextValue {
    setDashboardFormData: (dashboardFormData: DashboardFormData) => void;
    getDashboardFormData: () => DashboardFormData;
    toggleFilter: () => void;
    isFilterShown: boolean;
}

export const getDefaultDashboardFormData: () => DashboardFormData = () => ({
    materialType: getMaterialTypes()[0],
    definition: Definition.HOT_ROLLED,
    shape: [Shape.FLAT],
    sort: [{ property: 'price', direction: SortDirection.ASCENDING }],
});

const defaultValue: DashboardFormContextValue = {
    setDashboardFormData: () => {
        // noop
    },
    getDashboardFormData: () => getDefaultDashboardFormData(),
    toggleFilter: () => null,
    isFilterShown: false,
};

const DashboardFormContext = createContext(defaultValue);

export const DashboardFormContextProvider = ({
    children,
    defaultFormData,
}: {
    children: ReactNode;
    defaultFormData?: DashboardFormData;
}) => {
    const [dashboardFormData, setDashboardFormData] = useState<DashboardFormData>(
        defaultFormData || getDefaultDashboardFormData()
    );
    const [filterVisibility, setFilterVisibility] = useState(false);

    const toggleFilter = useCallback(() => {
        setFilterVisibility((prevValue) => !prevValue);
    }, [setFilterVisibility]);

    const value = useMemo(
        () => ({
            getDashboardFormData: () => dashboardFormData,
            setDashboardFormData: (dashboardData: DashboardFormData) => {
                setDashboardFormData(dashboardData);
            },
            toggleFilter,
            isFilterShown: filterVisibility,
        }),
        [dashboardFormData, setDashboardFormData, toggleFilter, filterVisibility]
    );

    return <DashboardFormContext.Provider value={value}>{children}</DashboardFormContext.Provider>;
};

export const useDashboardFormContext = () => useContext(DashboardFormContext);
